import { type FingerPrint, getFingerprint, getGlovoFingerPrint } from './fingerprint'
import { getConfig } from '@/tools/config'
import { fetcher } from '@/tools/fetcher/client'

interface Device {
  id?: string
  urn?: string
  fingerprintsExpirationTime?: number
}

interface DeviceCreationPayload {
  fingerprints: FingerPrint[]
}

const DEVICE_INFO_KEY = 'glv_device'
const saveDeviceInfo = (device: Device) => {
  const { fingerprintsExpirationTime, urn } = device
  const payload = { fingerprintsExpirationTime, urn }
  localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(payload))
}

const getStoredDevice = (): Device | null => {
  const stored = localStorage.getItem(DEVICE_INFO_KEY)

  if (!stored) {
    return null
  }

  const { fingerprintsExpirationTime } = JSON.parse(stored)

  if (fingerprintsExpirationTime < Date.now()) {
    localStorage.removeItem(DEVICE_INFO_KEY)
    return null
  }

  return JSON.parse(stored)
}

export const createDevice = async (useFingerPrint = true, userId?: string) => {
  const { apiUrl } = getConfig()
  const stored = getStoredDevice()

  if (stored) {
    return stored.id || stored.urn
  }

  const fingerprints = [
    useFingerPrint
      ? { provider: 'FingerprintJS', fingerprint: await getFingerprint(userId) }
      : null,
    getGlovoFingerPrint(),
  ].filter(Boolean) as FingerPrint[]

  const payload: DeviceCreationPayload = {
    fingerprints: fingerprints,
  }

  const { data: device } = await fetcher.post<Device>(`${apiUrl}/identity/v4/devices`, payload)
  if (!device.urn) {
    throw new Error('Device creation failed')
  }

  saveDeviceInfo(device)

  return device.urn
}

export const updateDevice = async () => {
  const storedDevice = getStoredDevice()
  const { apiUrl } = getConfig()
  if (storedDevice) {
    const { urn } = storedDevice
    const fingerprint = await getFingerprint()
    const payload: DeviceCreationPayload = {
      fingerprints: [{ provider: 'FingerprintJS', fingerprint }],
    }

    const { data: device } = await fetcher.post<Device>(
      `${apiUrl}/identity/v4/devices/${urn}`,
      payload
    )

    if (!device.urn) {
      throw new Error('Device update failed')
    }

    saveDeviceInfo(device)
    return device.urn
  }
}

export const getDeviceUrn = () => {
  const storedDevice = getStoredDevice()

  return storedDevice?.urn || null
}
